import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import shared from './GarageContent.module.scss';
// The same status chips and search box as the auction inspections tab.
import toolbar from './AuctionInspections.module.scss';
import classes from './PurchaseCard.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import ProgressTracker from './ProgressTracker';
import ShipTracking from './ShipTracking';
import StatusTimeline from './StatusTimeline';
import AuthContext from '../../store/auth-context';
import { getUserPurchaseDetails, getTrackingDetails } from '../../services/purchaseService';
import { setCarDetail } from '../../store/reducers/carReducer';
import { formatDay, formatUsd } from '../../services/dealerGarage';
import placeholderImage from '../../media/images/emptyFavorites.png';

// Where the car page's Back link returns to.
const BACK_PATH = '/garage?source=auction&tab=purchases';

// Every step of an auction purchase, in order: PURCHASE_STATUSES in
// Backend/app/controllers/purchase.controller.js.
const STEPS = [
    'Payment Due',
    'Transporting to Storage',
    'Booking Shipment',
    'Shipment Confirmed',
    'In Transit',
    'Arrived at Destination Port',
    'Ready for Pickup',
];
const LAST_STEP = 'Ready for Pickup';

// Once staff have added the ship's IMO, its live position means something
// until it has docked here.
const TRACKABLE_STEPS = ['Booking Shipment', 'Shipment Confirmed', 'In Transit', 'Arrived at Destination Port'];

const SHIPPING_FIELDS = ['shipName', 'pickupPort', 'deliveryPort', 'estimatedPickupDate', 'estimatedArrivalDate'];

const FILTERS = [
    { id: 'all', label: 'All', match: () => true },
    { id: 'due', label: 'Payment due', match: (status) => status === 'Payment Due' },
    { id: 'moving', label: 'On the way', match: (status) => STEPS.includes(status) && status !== 'Payment Due' && status !== LAST_STEP },
    { id: 'ready', label: 'Ready for pickup', match: (status) => status === LAST_STEP },
];

// purchaseService fills a missing value with 'N/A'.
const known = (value) => (value && value !== 'N/A' ? value : '');

const statusTone = (status) => {
    if (status === 'Payment Due') return shared.toneWarning;
    if (status === LAST_STEP) return shared.toneSuccess;
    return STEPS.includes(status) ? shared.toneBrand : shared.toneMuted;
};

// Our processed photos first, then the auction site's own, then anything.
const carImage = (car) => {
    if (!car) return '';
    const detail = car.carDetail || {};
    const first = (list) => (Array.isArray(list) ? list[0] : '');
    return first(detail.vehicleDetailImages) || first(car.vehicleDetailImages)
        || detail.vehicleDetailImage || car.vehicleDetailImage
        || first(detail.orignalVehicleDetailImages) || detail.orignalVehicleDetailImage
        || detail.image || first(car.vehicleImages) || car.image || '';
};

/**
 * How to show the invoice: { href } for an https link or a same-origin path,
 * { text } for anything else. A javascript:/data: value handed to href would
 * run in the member's session, so only those two are ever clickable. This
 * mirrors isSafeInvoice() in Backend/app/controllers/purchase.controller.js,
 * which also accepts invoice numbers and stored paths - existing rows carry
 * all three, and older ones plain http links, so nothing is hidden: what is
 * not clickable is shown as text, which React escapes.
 */
const invoiceView = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    // Browsers ignore whitespace and control characters when resolving a
    // scheme, so "java\nscript:..." is tested with those stripped out.
    const collapsed = Array.from(raw)
        .filter((ch) => ch.charCodeAt(0) > 32 && ch.charCodeAt(0) !== 127 && !/\s/.test(ch))
        .join('');
    // A single leading letter is a Windows drive path, not a scheme.
    const scheme = collapsed.match(/^([a-z][a-z0-9+.-]+):/i);
    // "//host" and "/\host" are protocol-relative: another host.
    const dangerous = /^[/\\]{2}/.test(collapsed) || Boolean(scheme && scheme[1].toLowerCase() !== 'https');
    const sameOriginPath = /^\/(?![/\\])/.test(raw);
    if (!dangerous && collapsed && (/^https:/i.test(collapsed) || sameOriginPath)) return { href: raw };
    return { text: raw };
};

// transportDetail maps each step to the day it was reached; oldest first,
// steps reached the same day in workflow order.
const historyFrom = (transportDetail) => Object.entries(transportDetail || {})
    .filter(([status, at]) => status && at)
    .map(([status, at]) => ({ status, at }))
    .sort((a, b) => (new Date(a.at) - new Date(b.at)) || (STEPS.indexOf(a.status) - STEPS.indexOf(b.status)));

/**
 * One auction purchase: where the car is between payment and pickup, the ship
 * while it is at sea, the purchase facts and the step history. Open purchases
 * start expanded; one that is ready for pickup starts collapsed to the summary.
 */
const AuctionPurchaseCard = ({ purchase }) => {
    const dispatch = useDispatch();
    const status = purchase.status || '';
    const [expanded, setExpanded] = useState(status !== LAST_STEP);

    const car = purchase.carDetails || {};
    // NFKC folds the feed's full-width chassis codes ("ＪＺＳ１７１").
    const title = (known(purchase.name) || 'Auction car').normalize('NFKC');
    const image = carImage(car) || placeholderImage;
    const lot = [known(purchase.auction), known(purchase.bidNo) && `Lot ${purchase.bidNo}`].filter(Boolean).join(' · ');
    const shipping = purchase.shippingDetail || {};
    const hasShipping = SHIPPING_FIELDS.some((field) => shipping[field]);
    const imo = String(shipping.imo || '').trim();
    const trackable = TRACKABLE_STEPS.includes(status) && /^\d{7}$/.test(imo);
    const vin = known(purchase.vin);
    const grade = known(purchase.grade);
    const amount = Number(purchase.payment) > 0 ? formatUsd(purchase.payment) : '';
    const invoice = invoiceView(purchase.invoice);
    const history = historyFrom(purchase.transportDetail);
    const detailsId = `auction-purchase-details-${purchase.id}`;

    const vehicleBody = (
        <>
            <img
                src={image}
                alt={title}
                loading="lazy"
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }}
            />
            <div className={shared.vehicleText}>
                <strong>{title}</strong>
                {lot && <span>{lot}</span>}
            </div>
        </>
    );

    return (
        <article className={shared.card}>
            <div className={shared.cardHead}>
                <div>
                    <span className={shared.typeTag}>Auction purchase</span>
                    {(purchase.purchaseDate || purchase.createdAt) && (
                        <span className={shared.date}>Bought {formatDay(purchase.purchaseDate || purchase.createdAt)}</span>
                    )}
                </div>
                <span className={`${shared.status} ${statusTone(status)}`}>{status || 'Pending'}</span>
            </div>

            {/* The detail page reads the car from the store when one is set;
                clear it so the page loads this car fresh. */}
            {car._id ? (
                <Link
                    to={`/detail/${car._id}`}
                    state={{ from: BACK_PATH }}
                    onClick={() => dispatch(setCarDetail(null))}
                    className={shared.vehicle}
                >
                    {vehicleBody}
                </Link>
            ) : (
                <div className={shared.vehicle}>{vehicleBody}</div>
            )}

            <ProgressTracker plan={STEPS} status={status} pipeline={STEPS} />

            {expanded && (
                <div id={detailsId}>
                    {(hasShipping || trackable) && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-ship" aria-hidden="true"></i>
                                Shipping
                            </h3>
                            {hasShipping && (
                                <dl className={shared.meta}>
                                    {shipping.shipName && <div><dt>Vessel</dt><dd>{shipping.shipName}</dd></div>}
                                    {shipping.pickupPort && <div><dt>From</dt><dd>{shipping.pickupPort}</dd></div>}
                                    {shipping.deliveryPort && <div><dt>To</dt><dd>{shipping.deliveryPort}</dd></div>}
                                    {shipping.estimatedPickupDate && <div><dt>Departs</dt><dd>{formatDay(shipping.estimatedPickupDate)}</dd></div>}
                                    {shipping.estimatedArrivalDate && <div><dt>Arrives</dt><dd>{formatDay(shipping.estimatedArrivalDate)}</dd></div>}
                                </dl>
                            )}
                            {trackable && (
                                <ShipTracking trackId={imo} vesselName={shipping.shipName} load={getTrackingDetails} />
                            )}
                        </section>
                    )}

                    <section>
                        <h3 className={shared.subhead}>
                            <i className="fas fa-car" aria-hidden="true"></i>
                            Purchase details
                        </h3>
                        <dl className={shared.meta}>
                            {amount && <div><dt>Amount</dt><dd>{amount}</dd></div>}
                            <div>
                                <dt>Invoice</dt>
                                <dd>
                                    {invoice?.href && (
                                        <a href={invoice.href} target="_blank" rel="noopener noreferrer">View invoice</a>
                                    )}
                                    {invoice?.text}
                                    {!invoice && 'Not available yet'}
                                </dd>
                            </div>
                            {vin && <div><dt>VIN</dt><dd>{vin}</dd></div>}
                            {grade && <div><dt>Auction grade</dt><dd>{grade}</dd></div>}
                            {purchase.refId && <div><dt>Reference</dt><dd>{purchase.refId}</dd></div>}
                        </dl>
                    </section>

                    {history.length > 0 && (
                        <section>
                            <h3 className={shared.subhead}>
                                <i className="fas fa-history" aria-hidden="true"></i>
                                Timeline
                            </h3>
                            <StatusTimeline history={history} />
                        </section>
                    )}
                </div>
            )}

            <button
                type="button"
                className={classes.detailsToggle}
                aria-expanded={expanded}
                aria-controls={expanded ? detailsId : undefined}
                onClick={() => setExpanded((open) => !open)}
            >
                {expanded ? 'Hide details' : 'Show details & timeline'}
                <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true"></i>
            </button>
        </article>
    );
};

/**
 * My Garage > Auction > Purchases: the cars the member won at auction as
 * cards, in the same style as the dealer-stock purchases.
 */
const AuctionPurchases = () => {
    const { userData } = useContext(AuthContext);
    const userId = userData?.userId;
    const [purchases, setPurchases] = useState(null);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!userId) return undefined;
        let active = true;
        setFailed(false);
        getUserPurchaseDetails(userId)
            .then((list) => active && setPurchases(Array.isArray(list) ? list : []))
            .catch(() => active && setFailed(true));
        return () => { active = false; };
    }, [userId]);

    const visible = useMemo(() => {
        const match = FILTERS.find((f) => f.id === filter).match;
        const term = search.trim().toLowerCase();
        return (purchases || []).filter((p) => {
            if (!match(p.status)) return false;
            if (!term) return true;
            return [p.name, p.bidNo, p.auction, p.vin, p.refId]
                .some((v) => known(v) && String(v).toLowerCase().includes(term));
        });
    }, [purchases, filter, search]);

    if (failed) {
        return <div className={shared.error}>Could not load your auction purchases right now. Please try again in a moment.</div>;
    }
    if (purchases === null) {
        return <div className={shared.state}><Loading text="Purchases" /></div>;
    }
    if (purchases.length === 0) {
        return (
            <div className={shared.state}>
                <EmptyContentCard
                    header="No auction purchases yet"
                    message="Cars you win at auction show up here, with each step from payment to pickup and the ship's position while it is at sea."
                />
                <Link to="/auction" className={shared.primaryLink}>Browse Auctions</Link>
            </div>
        );
    }

    return (
        <>
            <div className={toolbar.toolbar}>
                <div className={toolbar.filters} role="group" aria-label="Show">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            type="button"
                            aria-pressed={filter === f.id}
                            className={`${toolbar.filter} ${filter === f.id ? toolbar.filterActive : ''}`}
                            onClick={() => setFilter(f.id)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <label className={toolbar.search}>
                    <i className="fas fa-search" aria-hidden="true"></i>
                    <span className={shared.srOnly}>Search purchases</span>
                    <input
                        type="search"
                        placeholder="Car, lot, auction or VIN"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>
            </div>
            {visible.length === 0 ? (
                <p className={toolbar.none}>No purchases match.</p>
            ) : (
                <div className={shared.list}>
                    {visible.map((purchase) => (
                        <AuctionPurchaseCard key={purchase.id} purchase={purchase} />
                    ))}
                </div>
            )}
        </>
    );
};

export default AuctionPurchases;
